import { splitSentences, wordsOf } from "@/lib/tool-kit/publishing/text-checks";

/**
 * Plain-language (lay) summaries: assembling the draft, and checking it.
 *
 * The readability numbers are the Flesch–Kincaid grade level (Kincaid et al., 1975):
 *
 *   grade = 0.39 × (words ÷ sentences) + 11.8 × (syllables ÷ words) − 15.59
 *
 * with the same vowel-group syllable heuristic as the site's readability checker, so the
 * two tools agree on a word. Sentences are split only at an end mark followed by a space
 * (see `splitSentences`), so "1.5 times" stays one sentence.
 */

/* ---------------------------------------------------------------- syllables */

const RE_NON_LETTER = /[^\p{L}\p{M}]/gu;
const RE_COMBINING_MARK = /\p{M}/gu;
const RE_VOWEL_GROUPS = /[aeiouyıæœøаеиоуыэюяіїє]+/gu;

/** Vowel groups, minus a silent final "e" (but not "-le"); at least one per word. */
export function countSyllables(word: string): number {
  const bare = word
    .toLowerCase()
    .replace(RE_NON_LETTER, "")
    .normalize("NFD")
    .replace(RE_COMBINING_MARK, "");
  if (bare.length <= 2) {
    return 1;
  }
  let count = bare.match(RE_VOWEL_GROUPS)?.length ?? 1;
  if (bare.endsWith("e") && !bare.endsWith("le")) {
    count -= 1;
  }
  return Math.max(count, 1);
}

/* ---------------------------------------------------------------- readability */

export interface Readability {
  words: number;
  sentences: number;
  syllables: number;
  averageSentenceLength: number;
  /** Flesch–Kincaid grade level, floored at 0. */
  grade: number;
  /** Sentences of `longSentenceWords` words or more. */
  longSentences: { text: string; words: number }[];
}

/** Sentences this long or longer are listed for splitting. */
export const LONG_SENTENCE_WORDS = 25;

/** Cochrane's plain language summary guidance: aim for an average of about 20. */
export const TARGET_AVERAGE_SENTENCE = 20;

export function readability(text: string): Readability | null {
  const words = wordsOf(text);
  if (words.length === 0) {
    return null;
  }
  const sentenceList = splitSentences(text);
  const sentences = Math.max(sentenceList.length, 1);
  let syllables = 0;
  for (const word of words) {
    syllables += countSyllables(word);
  }
  const averageSentenceLength = words.length / sentences;
  const grade =
    0.39 * averageSentenceLength + 11.8 * (syllables / words.length) - 15.59;
  const longSentences = sentenceList
    .map((sentence) => ({ text: sentence, words: wordsOf(sentence).length }))
    .filter((sentence) => sentence.words >= LONG_SENTENCE_WORDS);
  return {
    words: words.length,
    sentences,
    syllables,
    averageSentenceLength,
    grade: Math.max(0, grade),
    longSentences,
  };
}

/* -------------------------------------------------------------------- jargon */

/**
 * Words and phrases that send a lay reader to a dictionary, with a plainer way to say
 * them. A curated list for English research writing — not exhaustive, and some terms
 * are exactly right in context ("placebo" may be the word the reader needs explained
 * rather than avoided), so a match is a prompt to reconsider, not an error.
 */
export const JARGON: readonly { forms: readonly string[]; plain: string }[] = [
  {
    forms: [
      "utilize",
      "utilizes",
      "utilized",
      "utilizing",
      "utilise",
      "utilised",
      "utilization",
    ],
    plain: "use",
  },
  {
    forms: ["facilitate", "facilitates", "facilitated", "facilitating"],
    plain: "help, make easier",
  },
  {
    forms: ["demonstrate", "demonstrates", "demonstrated", "demonstrating"],
    plain: "show",
  },
  { forms: ["approximately"], plain: "about" },
  { forms: ["subsequently"], plain: "later, then" },
  { forms: ["prior to"], plain: "before" },
  { forms: ["in order to"], plain: "to" },
  {
    forms: ["due to the fact that", "owing to the fact that"],
    plain: "because",
  },
  { forms: ["numerous"], plain: "many" },
  { forms: ["a majority of"], plain: "most" },
  { forms: ["commence", "commenced"], plain: "start, began" },
  { forms: ["terminate", "terminated"], plain: "end, stop" },
  { forms: ["methodology", "methodologies"], plain: "method, how we did it" },
  { forms: ["paradigm"], plain: "model, way of thinking" },
  { forms: ["efficacy"], plain: "how well it works" },
  { forms: ["cohort", "cohorts"], plain: "group of people" },
  { forms: ["participants"], plain: "people (who took part)" },
  {
    forms: ["statistically significant"],
    plain: "unlikely to be due to chance",
  },
  { forms: ["correlation", "correlated"], plain: "link, linked" },
  {
    forms: ["hypothesis", "hypothesized", "hypothesised"],
    plain: "idea we tested, expected",
  },
  { forms: ["prevalence"], plain: "how common" },
  { forms: ["incidence"], plain: "number of new cases" },
  { forms: ["mortality"], plain: "deaths" },
  { forms: ["morbidity"], plain: "illness" },
  { forms: ["etiology", "aetiology"], plain: "cause" },
  { forms: ["intervention", "interventions"], plain: "treatment, program" },
  { forms: ["adverse event", "adverse events"], plain: "harm, side effect" },
  { forms: ["longitudinal"], plain: "followed over time" },
  { forms: ["randomized", "randomised"], plain: "assigned by chance" },
  { forms: ["in vitro"], plain: "in the lab (in cells)" },
  { forms: ["in vivo"], plain: "in living animals or people" },
  { forms: ["robust"], plain: "strong, reliable" },
  { forms: ["novel"], plain: "new" },
  { forms: ["elucidate", "elucidated"], plain: "explain, made clear" },
  { forms: ["ameliorate", "ameliorated"], plain: "improve" },
  { forms: ["attenuate", "attenuated"], plain: "weaken, reduced" },
  { forms: ["exacerbate", "exacerbated"], plain: "make worse" },
  { forms: ["concomitant", "concomitantly"], plain: "at the same time" },
  { forms: ["heterogeneous", "heterogeneity"], plain: "varied, differences" },
  { forms: ["mitigate", "mitigated"], plain: "reduce" },
  { forms: ["optimal", "optimize", "optimise"], plain: "best, improve" },
  { forms: ["ascertain", "ascertained"], plain: "find out" },
  { forms: ["sufficient"], plain: "enough" },
  {
    forms: ["implement", "implemented", "implementation"],
    plain: "carry out, put in place",
  },
  { forms: ["parameters"], plain: "measures, limits" },
  { forms: ["quantify", "quantified"], plain: "measure" },
  { forms: ["predominantly"], plain: "mostly" },
  { forms: ["in the absence of"], plain: "without" },
  { forms: ["with regard to", "with respect to"], plain: "about" },
  {
    forms: ["is indicative of", "are indicative of"],
    plain: "shows, suggests",
  },
  { forms: ["has the ability to", "have the ability to"], plain: "can" },
  { forms: ["notwithstanding"], plain: "despite" },
  { forms: ["thereby"], plain: "so, which" },
  { forms: ["whereby"], plain: "by which" },
  { forms: ["aforementioned"], plain: "this, these" },
  { forms: ["empirical"], plain: "based on observation" },
  { forms: ["cognitive"], plain: "thinking, mental" },
  { forms: ["socioeconomic"], plain: "income and social position" },
  { forms: ["variables"], plain: "factors, things we measured" },
  { forms: ["meta-analysis"], plain: "combined analysis of several studies" },
  {
    forms: ["systematic review"],
    plain: "review of all the studies on a question",
  },
];

function escapeRegExp(text: string): string {
  return text.replace(RE_REGEX_SPECIAL, "\\$&");
}

const RE_REGEX_SPECIAL = /[.*+?^${}()|[\]\\]/g;
const RE_LITERAL_SPACE = / /g;

/** One case-insensitive whole-word matcher per entry, built once. */
const JARGON_MATCHERS = JARGON.map((entry) => ({
  term: entry.forms[0],
  plain: entry.plain,
  pattern: new RegExp(
    `(?<![\\p{L}\\p{N}-])(?:${entry.forms
      .map((form) => escapeRegExp(form).replace(RE_LITERAL_SPACE, "\\s+"))
      .join("|")})(?![\\p{L}\\p{N}-])`,
    "giu"
  ),
}));

export interface JargonHit {
  term: string;
  plain: string;
  count: number;
  /** The forms as they appear in the text, e.g. "Utilized", "utilizing". */
  found: string[];
}

/** Listed jargon present in the text, most frequent first. */
export function findJargon(text: string): JargonHit[] {
  const hits: JargonHit[] = [];
  for (const matcher of JARGON_MATCHERS) {
    const matches = text.match(matcher.pattern) ?? [];
    if (matches.length > 0) {
      hits.push({
        term: matcher.term,
        plain: matcher.plain,
        count: matches.length,
        found: [...new Set(matches)],
      });
    }
  }
  return hits.sort((a, b) => b.count - a.count);
}

/* ------------------------------------------------------------------- draft */

export type SummaryLayout = "paragraph" | "headed";

export interface SummarySection {
  heading: string;
  text: string;
}

const RE_RUNS_OF_SPACE = /\s+/g;

/** The answers as one paragraph, or under their headings. Empty answers are skipped. */
export function assembleSummary(
  sections: readonly SummarySection[],
  layout: SummaryLayout
): string {
  const filled = sections
    .map((section) => ({
      heading: section.heading,
      text: section.text.trim().replace(RE_RUNS_OF_SPACE, " "),
    }))
    .filter((section) => section.text !== "");
  if (layout === "paragraph") {
    return filled.map((section) => section.text).join(" ");
  }
  return filled
    .map((section) => `${section.heading}\n${section.text}`)
    .join("\n\n");
}

export interface LengthTarget {
  min: number;
  max: number;
}

export type LengthStatus = "under" | "within" | "over";

export function lengthStatus(
  words: number,
  target: LengthTarget
): LengthStatus {
  if (words > target.max) {
    return "over";
  }
  return words < target.min ? "under" : "within";
}
